import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {ThemeIcon} from 'src/design/atoms/ThemeIcon';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {IconAlertTriangle} from '@tabler/icons-react';
import React from 'react';
import type {InspectionScopeDeviceResponse} from 'src/api/ProactApiClient';
import {ComplianceStateEnum} from 'src/api/ProactApiClient';
import {formatDeviceLocation} from 'src/app/inspections/deviceRollup';
import {Pill} from 'src/design/atoms/Pill';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import type {VirtualizedTableColumn} from 'src/design/organisms/VirtualizedTable';
import {VirtualizedTable} from 'src/design/organisms/VirtualizedTable';
import {ComplianceStateEnumPill} from 'src/shared/constants/PillConstants';

// Up from the 56px VirtualizedTable default — generous touch target for gloved/imprecise field taps.
const INSPECTOR_ROW_HEIGHT = 64;

const DeviceCell: React.FC<{device: InspectionScopeDeviceResponse}> = ({device}) => {
  const name = device.name ?? `Device #${device.deviceId}`;
  return (
    <Box style={{minWidth: 0}}>
      <Group gap={4} wrap="nowrap">
        {/* Native title tooltip — cheap for virtualized rows; no per-row Mantine Tooltip. */}
        <Text size="sm" fw={600} truncate title={name}>
          {name}
        </Text>
        {!device.isVerified && (
          <Tooltip label="Added in the field — pending verification">
            <ThemeIcon size="sm" variant="transparent" color="yellow">
              <IconAlertTriangle size={14} />
            </ThemeIcon>
          </Tooltip>
        )}
        {device.deviceIsDeleted && (
          <Text span size="xs" c="dimmed">
            (deleted)
          </Text>
        )}
      </Group>
    </Box>
  );
};

// Mapped to the same human text the visible `Pill` renders — the raw numeric
// ComplianceStateEnum is meaningless spoken aloud.
const complianceStateLabel = (state?: ComplianceStateEnum): string => {
  switch (state) {
    case ComplianceStateEnum.Pass:
      return 'Pass';
    case ComplianceStateEnum.Fail:
      return 'Fail';
    case ComplianceStateEnum.NoAccess:
      return 'No Access';
    default:
      return 'Not inspected';
  }
};

const rowLabel = (device: InspectionScopeDeviceResponse) =>
  `${device.name ?? `Device #${device.deviceId}`}, ${complianceStateLabel(device.complianceState)}`;

export interface InspectorDeviceTableProps {
  devices: InspectionScopeDeviceResponse[];
  onEnterDevice: (device: InspectionScopeDeviceResponse) => void;
}

/**
 * Row-click device table for the guided flow's execute screen — the row itself is the
 * tap target, an exception scoped to this component. Every row navigates to `DeviceEntry`.
 */
export const InspectorDeviceTable: React.FC<InspectorDeviceTableProps> = ({
  devices,
  onEnterDevice,
}) => {
  const columns: VirtualizedTableColumn<InspectionScopeDeviceResponse>[] = [
    {
      id: 'device',
      header: 'Device',
      minWidth: 200,
      maxWidth: 340,
      render: device => <DeviceCell device={device} />,
    },
    {
      id: 'type',
      header: 'Type',
      minWidth: 150,
      maxWidth: 300,
      render: device => (
        <Text size="sm" c="dimmed" truncate>
          {device.equipmentTypeName ?? '—'}
        </Text>
      ),
    },
    {
      id: 'serial',
      header: 'Serial',
      width: 120,
      render: device => (
        <Text size="sm" c="dimmed" ff="monospace" truncate>
          {device.serialNumber ?? '—'}
        </Text>
      ),
    },
    {
      id: 'location',
      header: 'Location',
      minWidth: 160,
      maxWidth: 260,
      render: device => (
        <Text size="sm" c="dimmed" truncate>
          {formatDeviceLocation(device)}
        </Text>
      ),
    },
    {
      id: 'state',
      header: 'State',
      // Widest pill is "NOT INSPECTED" — 130 truncated it.
      width: 165,
      align: 'right',
      render: device => <Pill {...ComplianceStateEnumPill(device.complianceState)} />,
    },
  ];

  return (
    <VirtualizedTable
      columns={columns}
      items={devices}
      rowKey={device => device.deviceId}
      rowHeight={INSPECTOR_ROW_HEIGHT}
      stickyHeader
      height="100%"
      // Sum of the columns' minWidths — keeps the row content scrollable
      // rather than crushed below readable widths on narrower tablets.
      minWidth={795}
      onRowClick={onEnterDevice}
      getRowLabel={rowLabel}
      emptyState={<NoItemsPanel noItemsText="No devices match the current filters." />}
    />
  );
};
